/**
 * What You Can Rent — Home Page
 *
 * Four real products straight from the catalogue (photo, name, from-price)
 * so a first-time visitor can see what the business actually rents without
 * leaving the home page. Products flagged "featured" in the backoffice come
 * first; any remaining slots are filled in catalogue order. Works without a
 * location: the API returns each product's lowest price across locations
 * when none is selected.
 *
 * Mobile: horizontal scroll-snap rail. Desktop: one row of four.
 */

import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { useProducts } from '@/hooks/useCatalog';
import { useFormatPrice } from '@/hooks/useFormatPrice';
import { useLocale } from '@/i18n/useLocale';
import { buildPublishedLocaleUrl } from '@/i18n/routing';
import { getLowestTierPrice } from '@/lib/pricing';
import type { CustomerEquipmentTypeResponse } from '@/types';

const MAX_PRODUCTS = 4;
// Fetch more than we show so dropping photo-less items still fills the row.
const FETCH_SIZE = 12;
// Catalogue order: the same default sort the catalogue page uses.
const CATALOGUE_ORDER = { sortBy: 'recommended', sortOrder: 'asc' } as const;

// A product strip is only worth showing with photos: skip items without one.
const isShowable = (product: CustomerEquipmentTypeResponse) =>
    product.isAvailableForRental && !!product.primaryImageUrl;

const priceFor = (product: CustomerEquipmentTypeResponse): number | null =>
    product.lowestPrice ?? getLowestTierPrice(product);

export const WhatYouCanRentSection = () => {
    const featuredQuery = useProducts({ ...CATALOGUE_ORDER, isFeatured: true, pageSize: MAX_PRODUCTS });
    const catalogueQuery = useProducts({ ...CATALOGUE_ORDER, pageSize: FETCH_SIZE });
    const formatPrice = useFormatPrice();
    const locale = useLocale()?.locale ?? 'en';

    const isLoading = featuredQuery.isLoading || catalogueQuery.isLoading;
    const isError = featuredQuery.isError && catalogueQuery.isError;

    // Featured first, then top up from the catalogue (skipping duplicates).
    const featured = (featuredQuery.data?.data ?? []).filter(isShowable);
    const featuredIds = new Set(featured.map((p) => p.id));
    const fill = (catalogueQuery.data?.data ?? []).filter((p) => isShowable(p) && !featuredIds.has(p.id));
    const products = [...featured, ...fill].slice(0, MAX_PRODUCTS);

    if (isLoading) {
        return (
            <section className="bg-white py-14 sm:py-20" aria-label="What you can rent" role="status" aria-busy="true">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="delight-skeleton h-10 w-64 max-w-full rounded-full" aria-hidden="true" />
                    <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4" aria-hidden="true">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="overflow-hidden rounded-[1.5rem] border border-border/70 bg-white">
                                <div className="delight-skeleton aspect-[4/5]" />
                                <div className="space-y-2 p-4">
                                    <div className="delight-skeleton h-3 w-1/3 rounded-full" />
                                    <div className="delight-skeleton h-5 w-3/4 rounded-full" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (isError || products.length === 0) {
        return null;
    }

    const catalogueHref = buildPublishedLocaleUrl(locale, '/catalogue');

    return (
        <section aria-labelledby="what-you-can-rent-heading" className="bg-white py-14 sm:py-20">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
                    <div>
                        <h2
                            id="what-you-can-rent-heading"
                            className="font-display text-4xl leading-[0.96] tracking-tight text-foreground sm:text-5xl"
                        >
                            What you can rent
                        </h2>
                        <p className="mt-3 max-w-xl text-base leading-7 text-muted-foreground">
                            Car seats, strollers, travel cots, high chairs, carriers, and the small things that go with them.
                        </p>
                    </div>
                    <Link
                        to={catalogueHref}
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full text-sm font-semibold text-primary underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                        See the full catalogue
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                </div>

                <ul
                    className="-mx-4 mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:grid lg:grid-cols-4 lg:overflow-visible lg:px-0 lg:pb-0"
                    aria-label="Popular items to rent"
                >
                    {products.map((product) => {
                        const price = priceFor(product);
                        const href = buildPublishedLocaleUrl(locale, `/product/${product.slug || product.id}`);
                        const meta = [product.brand, product.ageRange].filter(Boolean).join(' · ');

                        return (
                            <li key={product.id} className="w-[70vw] max-w-[280px] flex-shrink-0 snap-start lg:w-auto lg:max-w-none">
                                <Link
                                    to={href}
                                    className="group flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-border/80 bg-white shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(15,118,110,0.14)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                    <div className="aspect-[4/5] overflow-hidden bg-secondary">
                                        <img
                                            src={product.primaryImageUrl}
                                            alt={product.name}
                                            className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col gap-1 p-4">
                                        {meta && (
                                            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">{meta}</p>
                                        )}
                                        <p className="font-display text-lg leading-tight text-foreground">{product.name}</p>
                                        {price != null && (
                                            <p className="mt-auto pt-2 text-sm text-muted-foreground">
                                                <span>from</span>{' '}
                                                <span className="font-display text-xl text-primary">{formatPrice(price)}</span>
                                            </p>
                                        )}
                                    </div>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
};

export default WhatYouCanRentSection;
